import { AccountType, PagedResult, User, UserFilter, UserGroup, UserGroupFilter } from "@infoland/iprova-api";
import TeamApi from "@/services/team";
import UserApi from "@/services/user";
import UserGroupApi from "@/services/usergroup";
import { ItemPoolResult, SystemItem, UserSelectionItem, UserSelectionType } from "./types";
import { Team, TeamFilter } from "@/teams";
import { SystemUserGroupIds, SystemUserIds } from "@/enums";

export interface SearchParams {
    /** term to filter name, login code and e-mail address by */
    nameLoginCodeEmailContains?: string;
    /** only get items from this user group */
    userGroupID?: string;
    /** the types of items to get */
    allowedTypes: UserSelectionType[];
    /** the ids of all the users that are allowed to be returned */
    allowedUserIDs?: string[];
    /** the ids of all the groups that are allowed to be returned */
    allowedUserGroupIDs?: string[];
    /** the ids of all the teams that are allowed to be returned */
    allowedTeamIDs?: string[];
    /** the ids of all the system items that are allowed to be returned */
    allowedSystemItems?: SystemItem[];
    /** the ids of any items that are not allowed to be returned */
    excludedIDs?: string[];
    /** Whether to exclude all users without an e-mail address */
    excludeWithoutEmailAddress?: boolean;
    /** Whether to exclude inactive users */
    excludeInactive?: boolean;
    /** The specific type of user to retrieve */
    userAccountType?: AccountType;
}

/** Typeguard for checking if an item is a user
 * @param item The item to check for type
 * @returns true if the item is a user, false otherwise
 */
export function isUser(item: UserSelectionItem): item is UserSelectionItem<User> {
    return item.type == "user";
}

/** Typeguard for checking if an item is a usergroup
 * @param item The item to check for type
 * @returns true if the item is a usergroup, false otherwise
 */
export function isUserGroup(item: UserSelectionItem): item is UserSelectionItem<UserGroup> {
    return item.type == "usergroup";
}

/** Typeguard for checking if an item is a team
 * @param item The item to check for type
 * @returns true if the item is a team, false otherwise
 */
export function isTeam(item: UserSelectionItem): item is UserSelectionItem<Team> {
    return item.type == "team";
}

export default class ItemPool {
    private readonly serverBatchSize: number = 100;

    private itemPool: UserSelectionItem[] = [];
    private serverOffset: number = 0;
    private clientResultSize: number = 0;
    private loadedUsers: number = 0;
    private loadedUserGroups: number = 0;
    private loadedTeams: number = 0;
    private clientBatchSize: number = 100;
    private canLoadMoreUsers: boolean = true;
    private canLoadMoreUserGroups: boolean = true;
    private canLoadMoreTeams: boolean = true;
    private userFilter: UserFilter;
    private userGroupFilter: UserGroupFilter;
    private teamFilter: TeamFilter;
    private requestNumber: number = 1;

    private searchParameters: SearchParams = undefined;

    /** Sets the size of the batch of users to return to the client side
     * @param batchSize The new batch size
     */
    public setBatchSize(batchSize: number): void {
        if (batchSize < 1) throw "Invalid batch size";
        this.clientBatchSize = batchSize;
    }

    /** sets new search parameters. Also clears the current results
     * @param parameters The new search parameters
     */
    public setSearchParameters(parameters: SearchParams): void {
        // clear offsets and items
        this.serverOffset = 0;
        this.clientResultSize = 0;
        this.itemPool = [];

        this.searchParameters = parameters;
        if (this.searchParameters.excludedIDs) this.searchParameters.excludedIDs = this.searchParameters.excludedIDs.map(i => i.toUpperCase());

        this.userFilter = this.createUserFilterFromSearchParameters();
        this.userGroupFilter = this.createUserGroupFilterFromSearchParameters();
        this.teamFilter = this.createTeamFilterFromSearchParameters();

        this.canLoadMoreUsers = !!this.userFilter;
        this.canLoadMoreUserGroups = !!this.userGroupFilter;
        this.canLoadMoreTeams = !!this.teamFilter;

        this.loadedUsers = 0;
        this.loadedUserGroups = 0;
        this.loadedTeams = 0;
    }

    /** makes sure any running request will be cancelled */
    public destroy(): void {
        this.requestNumber = 0;
    }

    /** Gets the next batch of user / group / team items from the server (if any more are present)
     * @returns a result containing the items and an indicator whether more items are available, or undefined if a network error has occurred
     */
    public async getItemsAsync(): Promise<ItemPoolResult> {
        if (this.requestNumber == 0) throw "Attempt to get items from a destroyed item pool";
        if (!this.searchParameters) throw "Attempt to get items without any search parameters";
        // number to keep track of whether this is in fact the latest request
        const myRequestNumber: number = ++this.requestNumber;

        const userPool: User[] = [];
        const userGroupPool: UserGroup[] = [];
        const teamPool: Team[] = [];

        let tasks: [Promise<User[]>, Promise<UserGroup[]>, Promise<Team[]>] = null;

        // keep going until enough users, groups and teams have been loaded, or until the request has become obsolete
        const minimumNeeded: number = this.clientResultSize + this.clientBatchSize;
        while (
            ((userPool.length + this.loadedUsers < minimumNeeded && this.canLoadMoreUsers) ||
                (userGroupPool.length + this.loadedUserGroups < minimumNeeded && this.canLoadMoreUserGroups) ||
                (teamPool.length + this.loadedTeams < minimumNeeded && this.canLoadMoreTeams)) &&
            myRequestNumber == this.requestNumber
        ) {
            // load all three types at the same time
            tasks = [this.getUsersFromServerAsync(), this.getUserGroupsFromServerAsync(), this.getTeamsFromServerAsync()];
            const results: [User[], UserGroup[], Team[]] = await Promise.all(tasks);

            // push the results into the pools (the task order is preserved in the results of promise.all)
            userPool.push(...results[0]);
            userGroupPool.push(...results[1]);
            teamPool.push(...results[2]);

            // update server offset
            this.serverOffset += this.serverBatchSize;
        }

        // don't return anything if this call was rendered obsolete by a follow-up call
        if (myRequestNumber != this.requestNumber) return null;

        if (userPool.length + userGroupPool.length + teamPool.length > 0) {
            // new data has been loaded

            // update data counts
            this.loadedUsers += userPool.length;
            this.loadedUserGroups += userGroupPool.length;
            this.loadedTeams += teamPool.length;

            // add users to item pool
            this.itemPool.push(
                ...userPool.map(user => {
                    return { item_id: user.user_id, name: user.name, type: <UserSelectionType>"user", data: user };
                })
            );

            // add usergroups to item pool
            this.itemPool.push(
                ...userGroupPool.map(userGroup => {
                    return { item_id: userGroup.user_group_id, name: userGroup.name, type: <UserSelectionType>"usergroup", data: userGroup };
                })
            );

            // add teams to item pool
            this.itemPool.push(
                ...teamPool.map(team => {
                    return { item_id: team.team_id, name: team.name, type: <UserSelectionType>"team", data: team };
                })
            );

            // re-sort the item pool (should be relatively fast because a larger part of it is already sorted)
            this.itemPool.sort((a, b) => a.name.localeCompare(b.name));
        }

        // increase client result size
        this.clientResultSize += this.clientBatchSize;

        // return the result
        const moreItemsAvailable: boolean =
            this.canLoadMoreUsers || this.canLoadMoreUserGroups || this.canLoadMoreTeams || this.itemPool.length > this.clientResultSize;

        return {
            items: this.clientBatchSize == Infinity ? this.itemPool : this.itemPool.slice(0, this.clientResultSize),
            moreItemsAvailable: moreItemsAvailable,
            containsUsers: this.loadedUsers > 0,
            containsUserGroups: this.loadedUserGroups > 0,
            containsTeams: this.loadedTeams > 0
        };
    }

    /** Creates a user filter from the search parameters currently in use
     * @returns a user filter object, or null if users should not be loaded
     */
    private createUserFilterFromSearchParameters(): UserFilter {
        if (!this.searchParameters.allowedTypes) return null;
        if (this.searchParameters.allowedTypes.indexOf("user") == -1) return null;

        const filter: UserFilter = {};

        filter.name_logincode_email_contains = this.searchParameters.nameLoginCodeEmailContains || undefined;
        filter.user_group_id = this.searchParameters.userGroupID || undefined;
        filter.user_ids = this.searchParameters.allowedUserIDs || undefined;
        filter.email = this.searchParameters.excludeWithoutEmailAddress ? { op: "pr" } : undefined;
        filter.active = this.searchParameters.excludeInactive !== false ? true : undefined; // odd, but excludeInactive can be undefined, which must be translated to true

        return filter;
    }

    /** Creates a usergroup filter from the search parameters currently in use
     * @returns a usergroup filter object, or null if usersgroups should not be loaded
     */
    private createUserGroupFilterFromSearchParameters(): UserGroupFilter {
        if (!this.searchParameters.allowedTypes) return null;
        if (this.searchParameters.allowedTypes.indexOf("usergroup") == -1) return null;

        const filter: UserGroupFilter = {};

        filter.name = this.searchParameters.nameLoginCodeEmailContains || undefined;
        filter.user_group_ids = this.searchParameters.allowedUserGroupIDs || undefined;

        return filter;
    }

    /** Creates a team filter from the search parameters currently in use
     * @returns a team filter object, or null if teams should not be loaded
     */
    private createTeamFilterFromSearchParameters(): UserGroupFilter {
        if (!this.searchParameters.allowedTypes) return null;
        if (this.searchParameters.allowedTypes.indexOf("team") == -1) return null;

        const filter: TeamFilter = {};

        filter.name = this.searchParameters.nameLoginCodeEmailContains || undefined;
        filter.team_ids = this.searchParameters.allowedTeamIDs || undefined;

        return filter;
    }

    /** Loads an extra set of users from the server
     * @returns The teams that were loaded from the server and then filtered
     */
    private async getUsersFromServerAsync(): Promise<User[]> {
        // don't do anything if there is no user filter, or it has been determined there are no more users
        if (!this.userFilter || !this.canLoadMoreUsers) return [];

        // attempt to get users. Do not suppress errors, so if the code continues, there is always a result
        const result: PagedResult<User> = await UserApi.getUsersAsync(this.userFilter, this.serverOffset, this.serverBatchSize, false);

        const finalResult: User[] = [];

        // store whether more users could possibly be loaded
        if (result.numberReturned < this.serverBatchSize) this.canLoadMoreUsers = false;

        // add system users to removal array if they are not explicitly allowed
        const idsToRemove: string[] = [];
        if (!this.searchParameters.allowedSystemItems || !this.searchParameters.allowedSystemItems.includes(SystemItem.administrator))
            idsToRemove.push(SystemUserIds.Administrator);

        if (!this.searchParameters.allowedSystemItems || !this.searchParameters.allowedSystemItems.includes(SystemItem.anonymous))
            idsToRemove.push(SystemUserIds.AnonymousUser);

        // add ids from the global excluded ids list
        if (this.searchParameters.excludedIDs) idsToRemove.push(...this.searchParameters.excludedIDs);

        // run through users once only to remove all unwanted results
        for (let i: number = 0; i < result.items.length; i++) {
            const item: User = result.items[i];
            if (idsToRemove.length > 0) {
                const foundIndex: number = idsToRemove.indexOf(item.user_id.toUpperCase());
                if (foundIndex > -1) {
                    idsToRemove.splice(foundIndex, 1);
                    // continue loop without adding this user to the final result
                    continue;
                }
            }

            // when filtering on account type, and the user is the wrong type, continue loop without adding this user to the final result
            if (this.searchParameters.userAccountType && item.account_type != this.searchParameters.userAccountType) continue;

            finalResult.push(item);
        }

        // return loaded users
        return finalResult;
    }

    /** Loads an extra set of usergroups from the server
     * @returns The user groups that were loaded from the server and then filtered
     */
    private async getUserGroupsFromServerAsync(): Promise<UserGroup[]> {
        // don't do anything if there is no user group filter, or if no more user groups can be loaded
        if (!this.userGroupFilter || !this.canLoadMoreUserGroups) return [];

        // attempt to get users. Do not suppress errors, so if the code continues, there is always a result
        const result: PagedResult<UserGroup> = await UserGroupApi.getUserGroupsAsync(
            this.userGroupFilter,
            this.serverOffset,
            this.serverBatchSize,
            false
        );

        const finalResult: UserGroup[] = [];

        // store whether more usergroups could possibly be loaded
        if (result.numberReturned < this.serverBatchSize) this.canLoadMoreUserGroups = false;

        // add system usergroups to removal array if they are not explicitly allowed
        const idsToRemove: string[] = [];
        if (!this.searchParameters.allowedSystemItems || !this.searchParameters.allowedSystemItems.includes(SystemItem.allLoggedInUsers))
            idsToRemove.push(SystemUserGroupIds.LoggedInUsers);

        if (!this.searchParameters.allowedSystemItems || !this.searchParameters.allowedSystemItems.includes(SystemItem.allUsers))
            idsToRemove.push(SystemUserGroupIds.AllUsers);

        if (!this.searchParameters.allowedSystemItems || !this.searchParameters.allowedSystemItems.includes(SystemItem.managers))
            idsToRemove.push(SystemUserGroupIds.Managers);

        // add ids from the global excluded ids list
        if (this.searchParameters.excludedIDs) idsToRemove.push(...this.searchParameters.excludedIDs);

        // run through usergroups once only to remove all unwanted results
        for (let i: number = 0; i < result.items.length; i++) {
            const item: UserGroup = result.items[i];
            if (idsToRemove.length > 0) {
                const foundIndex: number = idsToRemove.indexOf(item.user_group_id.toUpperCase());
                if (foundIndex > -1) {
                    idsToRemove.splice(foundIndex, 1);
                    // continue loop without adding this usergroup to the final result
                    continue;
                }
            }
            finalResult.push(item);
        }

        // return loaded user groups
        return finalResult;
    }

    /** Loads an extra set of usergroups from the server
     * @returns The teams that were loaded from the server and then filtered
     */
    private async getTeamsFromServerAsync(): Promise<Team[]> {
        // don't do anything if there is no user group filter, or if no more user groups can be loaded
        if (!this.teamFilter || !this.canLoadMoreTeams) return [];

        // attempt to get users. Do not suppress errors, so if the code continues, there is always a result
        const result: PagedResult<Team> = await TeamApi.getTeamsAsync(this.teamFilter, true, this.serverOffset, this.serverBatchSize, false);

        const finalResult: Team[] = [];

        // store whether more teams could possibly be loaded
        if (result.numberReturned < this.serverBatchSize) this.canLoadMoreTeams = false;

        // add globally excluded ids to removal array
        const idsToRemove: string[] = [];
        if (this.searchParameters.excludedIDs) idsToRemove.push(...this.searchParameters.excludedIDs);

        // run through teams once only to remove all unwanted results
        for (let i: number = 0; i < result.items.length; i++) {
            const item: Team = result.items[i];
            if (idsToRemove.length > 0) {
                const foundIndex: number = idsToRemove.indexOf(item.team_id.toUpperCase());
                if (foundIndex > -1) {
                    idsToRemove.splice(foundIndex, 1);
                    // continue loop without adding this team to the final result
                    continue;
                }
            }
            finalResult.push(item);
        }

        // return loaded teams
        return finalResult;
    }
}
